import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { Input } from '../../../components/ui';
import { getPreviousInvoices } from '../../../api/services/orderService';

const Title = styled.h1`
  width: 100%;
  font-size: 24px;
  margin-bottom: 1.5rem;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 1rem;
  }
`;

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin: 2rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InputLabel = styled.label`
  font-size: 14px;
  color: #4a4a4a;
`;

const InputContainer = styled.div`
  border: 1px solid #ddd;
  width: 100%;
  box-sizing: border-box;
`;

const SearchButton = styled.button`
  width: 100%;
  padding: 12px;
  margin-bottom: 2rem;
  background: ${(props) => props.theme.colors.primaryBlue};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  font-size: 16px;

  &:hover {
    background: #2b2370;
  }
`;

const InvoiceListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InvoiceListItem = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1.4fr 1fr;
  gap: 1.5rem;
  align-items: center;
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const InvoiceNumber = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.primaryText};
`;

const MutedText = styled.div`
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
`;

const AmountText = styled.div`
  font-size: 18px;
  font-weight: 700;
  text-align: right;
  color: ${(props) => props.theme.colors.primaryBlue};

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const NoInvoicesMessage = styled.div`
  text-align: center;
  color: ${(props) => props.theme.colors.secondaryText};
  font-size: 16px;
  padding: 2rem;
  border: 1px solid #eee;
  border-radius: 4px;
`;

const formatDate = (dateValue) => {
  if (!dateValue) {
    return '-';
  }

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return dateValue;
  }

  return date.toLocaleDateString('en-US');
};

const formatAmount = (amountValue, currencyCode) => {
  const amount = Number(amountValue);
  if (Number.isNaN(amount)) {
    return amountValue || '-';
  }

  return `${currencyCode || 'USD'} ${amount.toFixed(2)}`;
};

const PreviousInvoiceView = () => {
  const userInfo = useSelector((state) => state.userInfo);
  const [invoiceList, setInvoiceList] = useState([]);
  const [filter, setFilter] = useState({
    from: '',
    to: '',
    invoiceNo: '',
    orderNo: '',
    address: '',
  });

  const getDbInvoiceList = async () => {
    const result = await getPreviousInvoices(userInfo.idCustomer, filter);
    setInvoiceList(result.invoiceList || []);
  };

  return (
    <>
      <Title>Search Previous Invoices</Title>

      <InputGrid>
        <InputGroup>
          <InputLabel>From Date</InputLabel>
          <InputContainer>
            <Input
              type='date'
              placeholder='month/day/year'
              value={filter.from}
              onChange={(value) => {
                setFilter({ ...filter, from: value });
              }}
            />
          </InputContainer>
        </InputGroup>

        <InputGroup>
          <InputLabel>To Date</InputLabel>
          <InputContainer>
            <Input
              type='date'
              placeholder='month/day/year'
              value={filter.to}
              onChange={(value) => {
                setFilter({ ...filter, to: value });
              }}
            />
          </InputContainer>
        </InputGroup>

        <InputGroup>
          <InputLabel>Invoice Number</InputLabel>
          <InputContainer>
            <Input
              placeholder='Enter Invoice Number'
              value={filter.invoiceNo}
              onChange={(value) => {
                setFilter({ ...filter, invoiceNo: value });
              }}
            />
          </InputContainer>
        </InputGroup>

        <InputGroup>
          <InputLabel>Purchase Order Number</InputLabel>
          <InputContainer>
            <Input
              placeholder='Enter Purchase Order Number'
              value={filter.orderNo}
              onChange={(value) => {
                setFilter({ ...filter, orderNo: value });
              }}
            />
          </InputContainer>
        </InputGroup>
      </InputGrid>

      <InputGroup style={{ width: '100%', marginBottom: '2rem' }}>
        <InputLabel>Address</InputLabel>
        <InputContainer style={{ width: '100%' }}>
          <Input
            placeholder='Enter Address'
            value={filter.address}
            onChange={(value) => {
              setFilter({ ...filter, address: value });
            }}
          />
        </InputContainer>
      </InputGroup>

      <SearchButton onClick={getDbInvoiceList}>SEARCH</SearchButton>

      {invoiceList.length > 0 ? (
        <InvoiceListContainer>
          {invoiceList.map((invoice, index) => (
            <InvoiceListItem
              key={`${invoice.invoiceNumber || 'invoice'}-${index}`}
            >
              <Section>
                <InvoiceNumber>
                  Invoice # {invoice.invoiceNumber || '-'}
                </InvoiceNumber>
                <MutedText>
                  Invoice Date: {formatDate(invoice.invoiceDate)}
                </MutedText>
                <MutedText>Due Date: {formatDate(invoice.dueDate)}</MutedText>
              </Section>

              <Section>
                <MutedText>
                  <strong>Cust PO #:</strong>{' '}
                  {invoice.purchaseOrderNumber || '-'}
                </MutedText>
                <MutedText>
                  <strong>Address:</strong> {invoice.address || '-'}
                </MutedText>
              </Section>

              <AmountText>
                {formatAmount(invoice.totalAmount, invoice.currencyCode)}
              </AmountText>
            </InvoiceListItem>
          ))}
        </InvoiceListContainer>
      ) : (
        <NoInvoicesMessage>No invoices</NoInvoicesMessage>
      )}
    </>
  );
};

export default PreviousInvoiceView;
